import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Clock,
  Video,
  Mic,
  CheckCircle2,
  XCircle,
  UserCircle2,
  DoorOpen,
  ShieldAlert,
  Info,
} from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import DisputeFormModal from './DisputeFormModal';
import {
  getWaitingState,
  postSessionHeartbeat,
  joinSession,
  postCreatorConsent,
} from '../api/sessions';

/**
 * Phase 38A.4 · UniversalWaitingRoom
 *
 * Session-agnostic waiting screen used before every private realtime
 * session (One-to-One in this phase; Co-host + future session types later reuse
 * the same component). Public Live viewers do NOT go through this
 * component — see App.js routing.
 *
 * Contract:
 *    <UniversalWaitingRoom
 *        sessionId="NYM-OTO-..."
 *        onJoin={(joinBundle) => ...} // called with LiveKit token+url
 *        onLeave={() => ...}          // called when user clicks "Torna indietro"
 *    />
 */

const POLL_STATE_MS = 3000;
const HEARTBEAT_MS = 5000;

const fmtTime = (iso, locale = 'it-IT') =>
  new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

const pad2 = (n) => String(Math.max(0, Math.floor(n))).padStart(2, '0');

/** Return a formatted countdown between now and `targetIso`. */
const useCountdown = (targetIso, serverTimeIso) => {
  const [now, setNow] = useState(() => Date.now());
  // Server drift offset — trust the server's clock as source of truth.
  const driftRef = useRef(0);
  useEffect(() => {
    if (!serverTimeIso) return;
    const server = new Date(serverTimeIso).getTime();
    driftRef.current = server - Date.now();
  }, [serverTimeIso]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!targetIso) return null;
    const target = new Date(targetIso).getTime();
    const diff = Math.round((target - (now + driftRef.current)) / 1000);
    if (Number.isNaN(diff)) return null;
    if (diff <= 0) return { totalSeconds: 0, label: '00:00:00', past: true };
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    const label = `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
    return { totalSeconds: diff, label, past: false };
  }, [now, targetIso]);
};

const PresenceCard = ({ label, avatarUrl, username, present, isSelf, testId, t }) => (
  <div
    className={`flex items-center gap-3 rounded-2xl border p-4 transition-all ${
      present
        ? 'border-emerald-400/40 bg-emerald-500/5'
        : 'border-border bg-background/50'
    }`}
    data-testid={testId}
  >
    <div className="relative">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={username || label}
          className="h-11 w-11 shrink-0 rounded-full object-cover object-center"
        />
      ) : (
        <UserCircle2 className="h-11 w-11 text-muted-foreground" />
      )}
      <span
        className={`absolute -right-0.5 -bottom-0.5 h-3.5 w-3.5 rounded-full border-2 border-background ${
          present ? 'bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.75)]' : 'bg-neutral-500'
        }`}
        data-testid={`${testId}-dot`}
      />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label} {isSelf && <span className="text-amber-300/70">· {t('presence.you')}</span>}
      </p>
      <p className="truncate text-sm font-medium">{username || '—'}</p>
      <p
        className={`text-xs font-medium ${
          present ? 'text-emerald-300' : 'text-muted-foreground'
        }`}
        data-testid={`${testId}-status`}
      >
        {present ? t('presence.in_room') : t('presence.not_in_room')}
      </p>
    </div>
  </div>
);

const StatePill = ({ phase, refunded, t }) => {
  const base = refunded
    ? {
        label: t('phase.refunded'),
        className: 'bg-red-500/10 text-red-200 border-red-500/30',
        phaseKey: 'no_show',
      }
    : null;
  const cfg = base || {
    waiting: {
      label: t('phase.waiting'),
      className: 'bg-amber-500/10 text-amber-200 border-amber-500/30',
      phaseKey: 'waiting',
    },
    entry_window: {
      label: t('phase.entry_window'),
      className: 'bg-emerald-500/10 text-emerald-200 border-emerald-500/30 animate-pulse',
      phaseKey: 'entry_window',
    },
    active: {
      label: t('phase.active'),
      className: 'bg-emerald-500/15 text-emerald-100 border-emerald-500/40',
      phaseKey: 'active',
    },
    ended: {
      label: t('phase.ended'),
      className: 'bg-neutral-500/10 text-neutral-300 border-neutral-500/30',
      phaseKey: 'ended',
    },
    no_show: {
      label: t('phase.no_show'),
      className: 'bg-red-500/10 text-red-300 border-red-500/30',
      phaseKey: 'no_show',
    },
  }[phase] || { label: phase, className: 'bg-neutral-500/10 text-neutral-300', phaseKey: phase };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium uppercase tracking-wider ${cfg.className}`}
      data-testid={`waiting-room-phase-${cfg.phaseKey}`}
    >
      {cfg.label}
    </span>
  );
};

const UniversalWaitingRoom = ({ sessionId, currentUserId, onJoin, onLeave }) => {
  const { t, i18n } = useTranslation('waiting_room');
  const locale = i18n.language === 'en' ? 'en-GB' : 'it-IT';
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const stateRef = useRef(null);
  stateRef.current = state;

  // Polling loop for waiting-state.
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const s = await getWaitingState(sessionId);
        if (!cancelled) {
          setState(s);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail || e?.message || 'unknown';
          setError(String(detail));
        }
      }
    };
    load();
    const t = setInterval(load, POLL_STATE_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [sessionId]);

  // Heartbeat loop — silently pings the server every 5s while we're on
  // the page. The server rejects non-participants automatically.
  useEffect(() => {
    if (!sessionId) return undefined;
    const beat = () => {
      postSessionHeartbeat(sessionId).catch(() => {});
    };
    beat();
    const t = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(t);
  }, [sessionId]);

  const countdown = useCountdown(state?.start_utc, state?.server_time_utc);
  const entryClosesCountdown = useCountdown(
    state?.entry_window?.closes_at_utc,
    state?.server_time_utc,
  );

  // Creator consent modal state (Phase 38A.5).
  const [creatorConsentAccepted, setCreatorConsentAccepted] = useState(false);
  const [submittingConsent, setSubmittingConsent] = useState(false);
  const creatorConsentRequired = Boolean(
    state?.session_type === 'oto'
    && state?.viewer_role === 'creator'
    && state?.consent?.creator_consent_required,
  );

  const handleCreatorConsent = async () => {
    if (!sessionId || submittingConsent || !creatorConsentAccepted) return;
    setSubmittingConsent(true);
    try {
      await postCreatorConsent(sessionId);
      // Force an immediate refresh of the waiting-state so the modal
      // closes without waiting for the poll cycle.
      const s = await getWaitingState(sessionId);
      setState(s);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'consent_failed';
      setError(String(detail));
    } finally {
      setSubmittingConsent(false);
    }
  };

  // Dispute modal state (Phase 38A.5 — buyer-side CTA when session
  // reached COMPLETED and we're still inside the 48h window).
  const [disputeOpen, setDisputeOpen] = useState(false);

  const handleJoin = async () => {
    if (!sessionId || joining) return;
    setJoining(true);
    try {
      const bundle = await joinSession(sessionId);
      if (onJoin) onJoin(bundle);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'join_failed';
      setError(String(detail));
    } finally {
      setJoining(false);
    }
  };

  // Loading / error early-outs.
  if (!state && !error) {
    return (
      <div
        className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-muted-foreground"
        data-testid="waiting-room-loading"
      >
        <Loader2 className="h-8 w-8 animate-spin gold-text" />
        <p className="text-sm">{t('loading')}</p>
      </div>
    );
  }
  if (error && !state) {
    return (
      <div
        className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center"
        data-testid="waiting-room-error"
      >
        <ShieldAlert className="h-9 w-9 text-red-400" />
        <p className="text-lg font-semibold">{t('error_title')}</p>
        <p className="max-w-md text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={onLeave} data-testid="waiting-room-back-error-btn">
          {t('cta.back')}
        </Button>
      </div>
    );
  }

  const {
    session_type: sessionType,
    phase,
    creator,
    presence,
    entry_window: entryWindow,
    can_join_now: canJoinNow,
    viewer_role: viewerRole,
    mode,
    duration_minutes: durationMinutes,
    title,
    start_utc: startUtc,
    end_utc: endUtc,
    session_id: publicSessionId,
  } = state;

  const isCreator = viewerRole === 'creator';
  const isUser = viewerRole === 'user';

  // Case A/B/C messaging when the session ends without both sides.
  // Also fires for REFUNDED because the O2O REFUNDED path is always a
  // no-show story from the buyer's perspective (Case B/C were refunded
  // BEFORE being downgraded from CREATOR_NO_SHOW/BOTH_NO_SHOW to
  // REFUNDED by the refund handler).
  const isTerminalNoShow =
    phase === 'no_show' || state.state === 'REFUNDED';
  const noShowMessage = (() => {
    if (!isTerminalNoShow) return null;
    if (state.state === 'USER_NO_SHOW') {
      return isCreator
        ? t('no_show_messages.user_no_show_creator')
        : t('no_show_messages.user_no_show_user');
    }
    if (state.state === 'CREATOR_NO_SHOW') {
      return isUser
        ? t('no_show_messages.creator_no_show_user')
        : t('no_show_messages.creator_no_show_creator');
    }
    if (state.state === 'BOTH_NO_SHOW') {
      return t('no_show_messages.both_no_show');
    }
    if (state.state === 'REFUNDED') {
      return isUser
        ? t('no_show_messages.refunded_user')
        : t('no_show_messages.refunded_creator');
    }
    return null;
  })();

  return (
    <div
      className="mx-auto max-w-3xl px-4 py-8"
      data-testid="waiting-room"
      data-session-id={publicSessionId}
      data-session-type={sessionType}
      data-viewer-role={viewerRole}
    >
      <div className="rounded-3xl border border-border bg-card p-6 md:p-8 shadow-2xl">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              {sessionType === 'oto' ? t('type.oto') : t('type.live')}
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-foreground" data-testid="waiting-room-title">
              {title}
            </h1>
            <p className="mt-1 text-xs font-mono text-muted-foreground" data-testid="waiting-room-session-id">
              {publicSessionId}
            </p>
          </div>
          <StatePill
            phase={phase}
            refunded={['REFUNDED', 'CREATOR_NO_SHOW', 'BOTH_NO_SHOW'].includes(state.state)}
            t={t}
          />
        </div>

        {/* Meta info row */}
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="rounded-2xl border border-border bg-background/50 p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('meta.start')}</p>
            <p className="mt-1 text-sm font-medium" data-testid="waiting-room-start-time">
              {fmtTime(startUtc, locale)}
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-background/50 p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('meta.end')}</p>
            <p className="mt-1 text-sm font-medium">{fmtTime(endUtc, locale)}</p>
          </div>
          <div className="rounded-2xl border border-border bg-background/50 p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('meta.duration')}</p>
            <p className="mt-1 text-sm font-medium">{durationMinutes} {t('meta.min')}</p>
          </div>
          <div className="rounded-2xl border border-border bg-background/50 p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('meta.mode')}</p>
            <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium">
              {mode === 'video' ? <Video className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              {mode === 'video' ? 'Video' : 'Audio'}
            </p>
          </div>
        </div>

        {/* Countdown */}
        {phase === 'waiting' && countdown && !countdown.past && (
          <div
            className="mt-6 rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/10 to-transparent p-6 text-center"
            data-testid="waiting-room-countdown-block"
          >
            <p className="text-[11px] uppercase tracking-wider text-amber-200/80">
              {t('countdown.eyebrow')}
            </p>
            <p
              className="mt-2 font-mono text-4xl font-semibold gold-text md:text-5xl"
              data-testid="waiting-room-countdown"
            >
              {countdown.label}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {t('countdown.entry_open_at', { time: fmtTime(entryWindow?.opens_at_utc, locale) })}
            </p>
          </div>
        )}

        {phase === 'entry_window' && entryClosesCountdown && !entryClosesCountdown.past && (
          <div
            className="mt-6 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent p-6 text-center"
            data-testid="waiting-room-entry-window-block"
          >
            <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-emerald-300">
              <Clock className="h-3.5 w-3.5" /> {t('phase.entry_window')}
            </p>
            <p
              className="mt-2 font-mono text-4xl font-semibold text-emerald-200 md:text-5xl"
              data-testid="waiting-room-entry-countdown"
            >
              {entryClosesCountdown.label}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {t('countdown.entry_hint', { time: fmtTime(entryWindow?.closes_at_utc, locale) })}
            </p>
          </div>
        )}

        {/* Presence cards */}
        {sessionType === 'oto' && (
          <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
            <PresenceCard
              label={t('presence.creator')}
              avatarUrl={creator?.avatar_url}
              username={creator?.username || creator?.display_name}
              present={presence?.creator_present}
              isSelf={isCreator}
              testId="waiting-room-presence-creator"
              t={t}
            />
            <PresenceCard
              label={t('presence.buyer')}
              avatarUrl={null}
              username={isUser ? t('presence.you') : t('presence.buyer')}
              present={presence?.user_present}
              isSelf={isUser}
              testId="waiting-room-presence-user"
              t={t}
            />
          </div>
        )}

        {/* Join CTA / terminal state */}
        <div className="mt-8 flex flex-col items-center gap-3">
          {creatorConsentRequired && !isTerminalNoShow && (
            <div
              className="w-full space-y-3 rounded-2xl border border-amber-500/25 bg-amber-500/5 p-4"
              data-testid="waiting-room-creator-consent-block"
            >
              <p className="text-xs font-medium uppercase tracking-wider text-amber-200">
                {t('consent.title')}
              </p>
              <label
                className="flex cursor-pointer items-start gap-2 text-xs leading-relaxed"
                data-testid="waiting-room-creator-consent-label"
              >
                <Checkbox
                  checked={creatorConsentAccepted}
                  onCheckedChange={(v) => setCreatorConsentAccepted(!!v)}
                  className="mt-0.5"
                  data-testid="waiting-room-creator-consent-checkbox"
                />
                <span>
                  {t('consent.notice', {
                    terms: '__TERMS_LINK__',
                    privacy: '__PRIVACY_LINK__',
                  }).split(/(__TERMS_LINK__|__PRIVACY_LINK__)/g).map((chunk, i) => {
                    if (chunk === '__TERMS_LINK__') {
                      return (
                        <a
                          key={`t${i}`}
                          href="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gold-text underline underline-offset-2"
                          data-testid="waiting-room-creator-consent-terms-link"
                        >{t('consent.terms')}</a>
                      );
                    }
                    if (chunk === '__PRIVACY_LINK__') {
                      return (
                        <a
                          key={`p${i}`}
                          href="/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gold-text underline underline-offset-2"
                          data-testid="waiting-room-creator-consent-privacy-link"
                        >{t('consent.privacy')}</a>
                      );
                    }
                    return <React.Fragment key={i}>{chunk}</React.Fragment>;
                  })}
                </span>
              </label>
              <Button
                onClick={handleCreatorConsent}
                disabled={!creatorConsentAccepted || submittingConsent}
                size="sm"
                className="gold-gradient"
                data-testid="waiting-room-creator-consent-submit"
              >
                {submittingConsent ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : null}
                {t('consent.submit')}
              </Button>
            </div>
          )}
          {isTerminalNoShow && (
            <div
              className="w-full rounded-2xl border border-red-500/20 bg-red-500/5 p-4 text-center"
              data-testid="waiting-room-no-show-block"
            >
              <XCircle className="mx-auto h-8 w-8 text-red-300" />
              <p className="mt-2 text-sm font-medium text-red-200">{noShowMessage}</p>
            </div>
          )}
          {phase === 'ended' && state.state !== 'REFUNDED' && (
            <div className="w-full rounded-2xl border border-neutral-500/20 bg-neutral-500/5 p-4 text-center">
              <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-300" />
              <p className="mt-2 text-sm font-medium text-emerald-200">
                {t('completed')}
              </p>
              {isUser && state.o2o_session_id && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => setDisputeOpen(true)}
                  data-testid="waiting-room-open-dispute-btn"
                >
                  {t('cta.report_issue')}
                </Button>
              )}
            </div>
          )}
          {canJoinNow && phase !== 'ended' && phase !== 'no_show' && !isTerminalNoShow && !creatorConsentRequired && (
            <Button
              className="gold-gradient min-w-[180px] gap-2"
              size="lg"
              onClick={handleJoin}
              disabled={joining}
              data-testid="waiting-room-join-btn"
            >
              {joining ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <DoorOpen className="h-4 w-4" />
              )}
              {joining ? t('cta.joining') : t('cta.join')}
            </Button>
          )}
          {!canJoinNow && phase === 'waiting' && (
            <p
              className="inline-flex items-center gap-2 text-xs text-muted-foreground"
              data-testid="waiting-room-not-yet-joinable"
            >
              <Info className="h-3.5 w-3.5" /> {t('cta.not_yet_joinable')}
            </p>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onLeave}
            data-testid="waiting-room-back-btn"
          >
            {t('cta.back')}
          </Button>
        </div>
      </div>
      {state?.o2o_session_id && (
        <DisputeFormModal
          open={disputeOpen}
          onClose={() => setDisputeOpen(false)}
          o2oSessionId={state.o2o_session_id}
        />
      )}
    </div>
  );
};

export default UniversalWaitingRoom;
